import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { displaySize, radii, textSize } from 'design/tokens';

/* ------------------------------------------------------------------ card */

/**
 * Forwards its ref and any extra DOM props: the transactions list is a Card
 * that is also a scroll container, so it needs both a ref and an onScroll.
 */
export const Card = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div ref={ref} className={`st-card${className ? ` ${className}` : ''}`} {...rest}>
    {children}
  </div>
));
Card.displayName = 'Card';

/** Serif heading. `md` is a card title, `lg` a page or modal title. */
export const Display: React.FC<{
  children: React.ReactNode;
  size?: keyof typeof displaySize;
  as?: 'h1' | 'h2' | 'h3';
  style?: React.CSSProperties;
}> = ({ children, size = 'md', as: Tag = 'h2', style }) => (
  <Tag className="st-display" style={{ fontSize: displaySize[size], ...style }}>
    {children}
  </Tag>
);

/* ------------------------------------------------------------------ mask */

/**
 * The last four digits of an account, rendered immediately after its name.
 *
 * Plaid stores the bare digits; the `••` prefix is added here so nothing has to
 * remember to strip it before comparing or persisting. Renders nothing at all
 * when there is no mask — on the Overview strip in Institutions mode the
 * element has to collapse rather than reserve space for an empty span.
 *
 * The glyphs are hidden from the accessible name (they read as literal dots)
 * and the spoken form rides alongside them, so a row announces as
 * "Chase Checking, ending 4821" rather than doubling the digits.
 */
export const AccountMask: React.FC<{
  mask?: string | null;
  /** `filter` = 11.5px in the account dropdown, `page` = 12px on Accounts. */
  size?: 'default' | 'filter' | 'page';
  /** Selected/active rows lift the mask one step to --st-ink5. */
  selected?: boolean;
  style?: React.CSSProperties;
}> = ({ mask, size = 'default', selected, style }) => {
  const digits = (mask ?? '').trim();
  if (!digits) return null;
  return (
    <>
      <span
        className="st-mask"
        data-size={size}
        data-tone={selected ? 'selected' : undefined}
        aria-hidden="true"
        style={style}
      >
        {`••${digits}`}
      </span>
      <span className="st-sr">{`, ending ${digits}`}</span>
    </>
  );
};

/* ------------------------------------------------------------------ menu */

/**
 * Closes on outside mousedown or Escape; the trigger renders inside the anchor.
 *
 * `alsoInside` covers a menu rendered through a portal, which is outside the
 * anchor's subtree in the DOM even though it belongs to it — without it, the
 * first click inside such a menu reads as a click outside and shuts it.
 */
export function useDismissable<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void,
  alsoInside?: React.RefObject<HTMLElement | null>,
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inAnchor = ref.current?.contains(target);
      const inPortal = alsoInside?.current?.contains(target);
      if (!inAnchor && !inPortal) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, alsoInside]);

  return ref;
}

export interface MenuOption<T> {
  label: string;
  value: T;
}

export function DropdownMenu<T>({
  label,
  value,
  options,
  onPick,
  width = 210,
  align = 'right',
}: {
  label: string;
  value: T;
  options: Array<MenuOption<T>>;
  onPick: (v: T) => void;
  width?: number;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismissable(open, close);
  const active = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        variant="soft"
        pressed={active && active.value != null}
        onClick={() => setOpen((o) => !o)}
      >
        {(active && active.value != null ? active.label : label)} ▾
      </Button>
      {open && (
        <div
          className="st-menu"
          style={{
            width,
            top: 36,
            right: align === 'right' ? 0 : 'auto',
            left: align === 'left' ? 0 : 'auto',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          {options.map((o, i) => (
            <button
              key={`${String(o.value)}-${i}`}
              type="button"
              className="st-menu-item"
              data-active={o.value === value ? 'true' : 'false'}
              onClick={() => {
                onPick(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ misc */

export const Dot: React.FC<{ color: string; size?: number }> = ({ color, size = 9 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: Math.max(2, Math.round(size / 3)),
      background: color,
      display: 'block',
      flex: '0 0 auto',
    }}
  />
);

/** Label / big number / caption tile used across the stat rows. */
export const Stat: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  valueColor?: string;
  bar?: { pct: number; color: string } | null;
  divider?: boolean;
  /**
   * A tip that sits beside the label — usually an `InfoDot`. Its own prop
   * rather than something folded into `label`, because the label row has to
   * become a flex line to hold it and a caller passing a fragment would have
   * to know that.
   */
  info?: React.ReactNode;
}> = ({ label, value, sub, valueColor, bar, divider, info }) => (
  <div
    style={
      divider
        ? { borderLeft: '1px solid var(--st-rule2)', paddingLeft: 28 }
        : undefined
    }
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: textSize.body,
        color: 'var(--st-ink3)',
        marginBottom: 8,
      }}
    >
      {label}
      {info}
    </div>
    <div className="st-num" style={{ fontSize: displaySize.sm, fontWeight: 600, color: valueColor }}>
      {value}
    </div>
    {bar ? (
      <div
        style={{
          height: 4,
          background: 'var(--st-rule3)',
          marginTop: 12,
          borderRadius: radii.hair,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 0,
            width: `${Math.max(0, Math.min(100, bar.pct))}%`,
            background: bar.color,
            borderRadius: radii.hair,
          }}
        />
      </div>
    ) : (
      <div style={{ height: 4, marginTop: 12 }} />
    )}
    {sub != null && (
      <div style={{ fontSize: textSize.body, color: 'var(--st-ink4)', marginTop: 9 }}>{sub}</div>
    )}
  </div>
);

/* -------------------------------------------------------------- feedback */

export const Skeleton: React.FC<{ height?: number; width?: number | string; radius?: number }> = ({
  height = 14,
  width = '100%',
  radius = 8,
}) => <span className="st-skeleton" style={{ display: 'block', height, width, borderRadius: radius }} />;

export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 180 }) => (
  <Card style={{ padding: '30px 34px' }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Skeleton width={160} height={18} />
      <Skeleton height={height} radius={14} />
    </div>
  </Card>
);

export const ErrorNote: React.FC<{ error: unknown; onRetry?: () => void }> = ({ error, onRetry }) => {
  const message = error instanceof Error ? error.message : 'Something went wrong loading this data.';
  return (
    <Card style={{ padding: '26px 30px' }}>
      <div style={{ fontSize: textSize.medium, color: 'var(--st-neg-text)', marginBottom: 6 }}>{message}</div>
      {onRetry && (
        <Button variant="soft" onClick={onRetry} style={{ marginTop: 8 }}>
          Try again
        </Button>
      )}
    </Card>
  );
};

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="st-empty">{children}</div>
);
